/**
 * The configuration the server hands the browser, read once at module load.
 *
 * Under Next.js these values were `NEXT_PUBLIC_*` environment variables inlined
 * into the bundle at build time, which meant changing one contract address
 * required rebuilding and republishing an image. The Go shell now writes them
 * into `index.html` on every request, so one image serves staging and
 * production and an address change is a restart.
 *
 * It arrives as `<script type="application/json">` rather than as an assignment
 * to `window`. A JSON data block is not executed and is not covered by
 * `script-src`, so it needs no CSP nonce — and, more importantly, a value that
 * happens to contain a quote cannot turn the tag into a script-injection point.
 *
 * Read synchronously at import time, because the tag is in `<head>` and this
 * module is loaded by the entry bundle below it. Every consumer can therefore
 * keep exporting plain `const`s, exactly as they did when the values were
 * inlined.
 */

export type RuntimeConfig = {
  chainId: number;
  claimMode: string;
  networkName: string;
  explorerBase: string;

  asetquToken: string | null;
  claimContract: string | null;
  legacyToken: string | null;
  permanentLockVault: string | null;
  utilityRegistry: string | null;
  staking: string | null;
  registrySafe: string | null;

  walletConnectProjectId: string;
  supportUrl: string;
  portalOrigin: string;

  /**
   * Where the Safe's owners sign: `https://multisig.bnbchain.org` on testnet,
   * `https://app.safe.global` on mainnet. Empty on a chain with no Safe
   * interface, which the registry console renders as "there is nowhere to sign
   * yet" rather than as a dead link.
   */
  safeAppUrl: string;
  /**
   * How that app addresses a Safe — the `bnb` in `bnb:0x1234…`.
   *
   * Sent by the server rather than derived here because it is per-app rather
   * than universal: BNB Chain's interface answers `bsc-testnet` for chain 97,
   * which is not the EIP-3770 registry's `bnbt`, and a wrong prefix produces a
   * Safe app that loads and then shows nothing.
   */
  safeAppPrefix: string;

  stakingEnabled: boolean;
};

export const CONFIG_ELEMENT_ID = "asetqu-config";

/**
 * What a page renders when the shell did not inject anything.
 *
 * Every address is null, which the UI already knows how to handle: it means
 * "not deployed", so each action reports that instead of offering a button
 * wired to nothing. That is the same safe failure the portal has always had for
 * an unset address, reached here by a different route.
 */
export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  chainId: 56,
  claimMode: "swap",
  networkName: "BNB Smart Chain",
  explorerBase: "https://bscscan.com",

  asetquToken: null,
  claimContract: null,
  legacyToken: null,
  permanentLockVault: null,
  utilityRegistry: null,
  staking: null,
  registrySafe: null,

  walletConnectProjectId: "",
  supportUrl: "",
  portalOrigin: "",

  safeAppUrl: "",
  safeAppPrefix: "",

  stakingEnabled: false,
};

let override: RuntimeConfig | null = null;
let memo: RuntimeConfig | null = null;

/**
 * Replaces the configuration for a test.
 *
 * Tests run in Node with no document to read from, so without this every module
 * that reads config would see the fallback. Pass `null` to restore it.
 *
 * Call it before importing the module under test: consumers capture the config
 * into module-level constants at their own import time, so `vi.resetModules()`
 * is what makes a second value take effect.
 */
export function setRuntimeConfigForTests(value: Partial<RuntimeConfig> | null): void {
  override = value === null ? null : { ...DEFAULT_RUNTIME_CONFIG, ...value };
  memo = null;
}

function read(): RuntimeConfig {
  if (override !== null) return override;

  if (typeof document === "undefined") return DEFAULT_RUNTIME_CONFIG;

  const element = document.getElementById(CONFIG_ELEMENT_ID);
  if (!element?.textContent) {
    // Loud, because the only way to get here in a browser is serving `dist/`
    // without the shell in front of it — and the page that results looks like a
    // portal whose contracts are all undeployed.
    console.error(
      `Runtime configuration is missing: no #${CONFIG_ELEMENT_ID} in the document. ` +
        "The portal is being served without its server shell, so no contract addresses are known.",
    );
    return DEFAULT_RUNTIME_CONFIG;
  }

  try {
    return { ...DEFAULT_RUNTIME_CONFIG, ...(JSON.parse(element.textContent) as Partial<RuntimeConfig>) };
  } catch (error) {
    console.error("Runtime configuration is not valid JSON:", error);
    return DEFAULT_RUNTIME_CONFIG;
  }
}

/**
 * The configuration, read on first use and cached.
 *
 * A function rather than a `const` evaluated at module load, and the reason is
 * ordering: a `const` would be computed the moment *this* module is first
 * imported, which in a test is before the case has had any chance to say what
 * the configuration should be. Consumers still capture it into module-level
 * constants — the laziness only has to outlive this module's own import.
 */
export function runtimeConfig(): RuntimeConfig {
  memo ??= read();
  return memo;
}
